<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { InputEmits, InputProps } from '@/deep-form/models/dform-input-core';
import { ColorRule, ColorRules, ColorSchemeIn, CONDITION_OPERATORS, Condition } from '@/utils/conditional-colors';
import * as src from '../lib/sources';
import { InputDictionaryItem } from '@/deep-form/components/InputDictionary';

const props = defineProps<InputProps<ColorRules, null>>();
const emit = defineEmits<InputEmits<ColorRules>>();

// Default model value
const defaultValue: ColorRules = {
  self: '',
  front: '',
  deadend: '',
  fields: []
};

// Local model to track the current value
const localValue = ref<ColorRules>(props.modelValue || defaultValue);

// Track whether to use JavaScript expressions or standard rules
const useJsExpressions = ref(false);

// Marketing properties for dropdowns
const marketingProperties = ref<InputDictionaryItem[]>([]);

// Fetch marketing properties
onMounted(async () => {
  marketingProperties.value = await src.propertiesMarketing();
});

// Track which color pickers are open
const colorPickerDialog = ref<Record<string, boolean>>({
  self: false,
  front: false,
  deadend: false
});

// Track which JS expressions have expanded property panels
const expandedPropertyPanels = ref<Record<number, boolean>>({});

// Search terms for properties in JavaScript expression mode (one per rule)
const propertySearchTerms = ref<Record<number, string>>({});

// Get filtered properties for a specific rule
const getFilteredProperties = (ruleIndex: number) => {
  const searchTerm = propertySearchTerms.value[ruleIndex] || '';
  if (!searchTerm) return marketingProperties.value;

  const lowerSearchTerm = searchTerm.toLowerCase();
  return marketingProperties.value.filter(prop => {
    const title = typeof prop === 'string' ? prop : prop.title;
    const value = typeof prop === 'string' ? prop : String(prop.value);
    return title.toLowerCase().includes(lowerSearchTerm) || value.toLowerCase().includes(lowerSearchTerm);
  });
};

// Standard rules and JS rules are stored separately
const standardRules = computed(() =>
  (localValue.value.fields || []).filter(rule => rule.length === 4)
);

const jsRules = computed(() =>
  (localValue.value.fields || []).filter(rule => rule.length === 2)
);

// Handlers for adding new rules
const addStandardRule = () => {
  if (!localValue.value.fields) {
    localValue.value.fields = [];
  }

  // Get the first property from marketingProperties, or empty string if not available
  const defaultProperty = marketingProperties.value.length > 0
    ? (typeof marketingProperties.value[0] === 'string'
        ? marketingProperties.value[0]
        : String(marketingProperties.value[0].value || ''))
    : '';

  // Add a new standard rule with default values [property, operator, value, color]
  localValue.value.fields.push([defaultProperty, '=', '0', '#000000']);
  updateModel();
};

const addJsRule = () => {
  if (!localValue.value.fields) {
    localValue.value.fields = [];
  }

  // Add a new JS expression rule [expression, color]
  localValue.value.fields.push(['', '']);
  updateModel();
};

// Remove a rule
const removeRule = (index: number, isJsRule: boolean) => {
  const rules = isJsRule ? jsRules.value : standardRules.value;
  const ruleToRemove = rules[index];

  if (!localValue.value.fields) return;

  const indexInOriginal = localValue.value.fields.findIndex(r => r === ruleToRemove);
  if (indexInOriginal >= 0) {
    localValue.value.fields.splice(indexInOriginal, 1);
    updateModel();
  }
};

// Update model value
const updateModel = () => {
  emit('update:modelValue', { ...localValue.value });
};

// Update simple color values
const updateColorValue = (key: keyof Pick<ColorRules, 'self' | 'front' | 'deadend'>, value: ColorSchemeIn) => {
  localValue.value[key] = value;
  updateModel();
};

// Update a specific field in a rule
const updateRule = (rule: ColorRule, index: number, value: any, isJsRule: boolean) => {
  if (isJsRule) {
    // For JS rules, we're updating either the expression or the color
    rule[index] = value;
  } else {
    // For standard rules, we're updating property, operator, value, or color
    rule[index] = value;
  }
  updateModel();
};

// Toggle property panel expansion for a specific rule
const togglePropertyPanel = (index: number) => {
  expandedPropertyPanels.value[index] = !expandedPropertyPanels.value[index];
};

// Add property to JS expression
const addPropertyToExpression = (rule: ColorRule, property: string, ruleIndex: number) => {
  if (rule.length !== 2) return;

  // Add property to the expression
  const currentExpression = rule[0] as string;

  // Find the cursor position safely
  const textField = document.activeElement as HTMLInputElement;
  let cursorPos = currentExpression.length;

  if (textField && 'selectionStart' in textField) {
    cursorPos = textField.selectionStart || currentExpression.length;
  }

  const newExpression =
    currentExpression.slice(0, cursorPos) +
    property +
    currentExpression.slice(cursorPos);

  rule[0] = newExpression;
  updateModel();

  // Clear search term after adding property
  propertySearchTerms.value[ruleIndex] = '';
};

// Format color for display
const formatColor = (color: any): string => {
  if (!color) return '';

  if (typeof color === 'string') {
    return color;
  }

  if (color.background) {
    return color.background;
  }

  return '';
};

// Clean the model when component is mounted to ensure proper structure
const ensureProperModel = () => {
  if (!localValue.value) {
    localValue.value = { ...defaultValue };
  }

  if (!localValue.value.fields) {
    localValue.value.fields = [];
  }

  updateModel();
};

// Call this on mounted
ensureProperModel();
</script>

<template>
  <div class="color-rules-editor">
    <!-- Simple color settings -->
    <v-row >
      <v-col cols="4">
        <v-text-field
          hide-details
          label="Self color"
          v-model="localValue.self"
          @click="colorPickerDialog.self = true"
          readonly
          :style="{ backgroundColor: formatColor(localValue.self), color: formatColor(localValue.self) ? '#fff' : undefined }"
        />
        <v-dialog v-model="colorPickerDialog.self" max-width="360px">
          <v-card>
            <v-card-title>Select Self Color</v-card-title>
            <v-card-text class="pa-0">
              <v-color-picker class="w-100" width="100%" elevation="0"
                v-model="localValue.self"
                @update:model-value="updateColorValue('self', $event); colorPickerDialog.self = false"
                mode="hexa"
                show-swatches
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="colorPickerDialog.self = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
      <v-col cols="4">
        <v-text-field
          hide-details
          label="Frontline color"
          v-model="localValue.front"
          @click="colorPickerDialog.front = true"
          readonly
          :style="{ backgroundColor: formatColor(localValue.front), color: formatColor(localValue.front) ? '#fff' : undefined }"
        />
        <v-dialog v-model="colorPickerDialog.front" max-width="360px">
          <v-card>
            <v-card-title>Select Frontline Color</v-card-title>
            <v-card-text class="pa-0">
              <v-color-picker class="w-100" width="100%" elevation="0"
                v-model="localValue.front"
                @update:model-value="updateColorValue('front', $event); colorPickerDialog.front = false"
                mode="hexa"
                show-swatches
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="colorPickerDialog.front = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
      <v-col cols="4">
        <v-text-field
          hide-details
          label="Deadend color"
          v-model="localValue.deadend"
          @click="colorPickerDialog.deadend = true"
          readonly
          :style="{ backgroundColor: formatColor(localValue.deadend), color: formatColor(localValue.deadend) ? '#fff' : undefined }"
        />
        <v-dialog v-model="colorPickerDialog.deadend" max-width="360px">
          <v-card>
            <v-card-title>Select Deadend Color</v-card-title>
            <v-card-text class="pa-0">
              <v-color-picker class="w-100" width="100%" elevation="0"
                v-model="localValue.deadend"
                @update:model-value="updateColorValue('deadend', $event); colorPickerDialog.deadend = false"
                mode="hexa"
                show-swatches
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="colorPickerDialog.deadend = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>

    <!-- Tabs for rules type selection -->
    <v-tabs
      v-model="useJsExpressions"
      class="mt-4 tabs-container custom-tabs"
      bg-color="background"
      slider-color="primary"
    >
      <v-tab :value="false" :class="{ 'active-tab': !useJsExpressions }">
        Standard Rules
        <v-badge
          :content="standardRules.length.toString()"
          color="primary"
          inline
          class="ml-2"
        ></v-badge>
      </v-tab>
      <v-tab :value="true" :class="{ 'active-tab': useJsExpressions }">
        Advanced Rules
        <v-badge
          :content="jsRules.length.toString()"
          color="primary"
          inline
          class="ml-2"
        ></v-badge>
      </v-tab>
    </v-tabs>

    <!-- Note for JavaScript expressions -->
    <v-card-text v-if="useJsExpressions" class="text-caption mt-4">
      <strong>Note:</strong> For JavaScript expressions, use backtick in cases like - p.title.toString().includes(`A`). <br/>
       `A` instead of 'A' or "A" for proper insertion in HTML template
    </v-card-text>

    <!-- Standard rules section -->
    <div v-if="!useJsExpressions" class="standard-rules mt-4">
      <v-card-subtitle>Format: [property, operator, value, color]</v-card-subtitle>

      <div v-for="(rule, index) in standardRules" :key="`standard-${index}`" class="rule-item mb-2">
        <v-row no-gutters>
          <v-col cols="4">
            <v-select
              label="Property"
              :model-value="rule[0]"
              @update:model-value="updateRule(rule, 0, $event, false)"
              :items="marketingProperties"
              item-title="title"
              item-value="value"
              class="mr-md-4"
              hide-details
            />
          </v-col>
          <v-col cols="2">
            <v-select
              label="Operator"
              :items="CONDITION_OPERATORS"
              :model-value="rule[1] as Condition"
              @update:model-value="updateRule(rule, 1, $event, false)"
              class="mr-md-4"
              hide-details
            >
              <template #selection>
                <span>{{ rule[1] }}</span>
              </template>
            </v-select>
          </v-col>
          <v-col cols="3">
            <v-text-field
              label="Value"
              :model-value="rule[2]"
              @update:model-value="updateRule(rule, 2, $event, false)"
              class="mr-md-4"
              hide-details
            />
          </v-col>
          <v-col cols="2">
            <v-text-field
              readonly
              :model-value="formatColor(rule[3])"
              @click="colorPickerDialog[`standard_${index}`] = true"
              :style="{ backgroundColor: formatColor(rule[3]), color: formatColor(rule[3]) ? '#fff' : undefined }"
              label="Color"
              hide-details
            />
            <v-dialog v-model="colorPickerDialog[`standard_${index}`]" max-width="360px">
              <v-card>
                <v-card-title>Select Color</v-card-title>
                <v-card-text class="pa-0">
                  <v-color-picker class="w-100" width="100%" elevation="0"
                    :model-value="rule[3]"
                    @update:model-value="updateRule(rule, 3, $event, false); colorPickerDialog[`standard_${index}`] = false"
                    mode="hexa"
                    show-swatches
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" text @click="colorPickerDialog[`standard_${index}`] = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
          <v-col cols="1">
            <v-btn
              icon
              variant="text"
              color="error"
              @click="removeRule(index, false)"
              class="mt-2"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <v-btn variant="flat" color="primary" @click="addStandardRule" class="mt-3">
        Add Standard Rule
      </v-btn>
    </div>

    <!-- JavaScript expression rules section -->
    <div v-else class="js-rules mt-4">
      <v-card-subtitle>Format: [expression, color]</v-card-subtitle>

      <div v-for="(rule, index) in jsRules" :key="`js-${index}`" class="rule-item mb-6">
        <v-row no-gutters>
          <v-col cols="9">
            <div class="d-flex align-center">
              <v-text-field
                label="JavaScript Expression"
                :model-value="rule[0]"
                @update:model-value="updateRule(rule, 0, $event, true)"
                placeholder="e.g. (m.Status > 2) && (m.lCO < 3000)"
                class="mr-md-2 flex-grow-1"
              />
            </div>

            <!-- Collapsible property list -->
            <v-card class="mt-2" variant="outlined">
              <v-card-title class="d-flex py-2">
                <span class="text-subtitle-2">Marketing Properties</span>
                <v-spacer></v-spacer>
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  @click="togglePropertyPanel(index)"
                >
                  <v-icon>
                    {{ expandedPropertyPanels[index] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                </v-btn>
              </v-card-title>

              <v-expand-transition>
                <div v-if="expandedPropertyPanels[index]">
                  <v-divider></v-divider>
                  <v-card-text class="pa-2">
                    <v-text-field
                      v-model="propertySearchTerms[index]"
                      label="Search properties"
                      prepend-inner-icon="mdi-magnify"
                      density="compact"
                      variant="outlined"
                      clearable
                    />

                    <v-list density="compact" height="200" class="property-list overflow-y-auto">
                      <v-list-item
                        v-for="property in getFilteredProperties(index)"
                        :key="typeof property === 'string' ? property : String(property.value)"
                        @click="addPropertyToExpression(rule, typeof property === 'string' ? property : String(property.value), index)"
                        class="property-item"
                      >
                        <v-list-item-title>{{ typeof property === 'string' ? property : property.title }}</v-list-item-title>
                        <v-list-item-subtitle>{{ typeof property === 'string' ? property : String(property.value) }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </div>
              </v-expand-transition>
            </v-card>
          </v-col>
          <v-col cols="2">
            <v-text-field
              readonly
              :model-value="formatColor(rule[1])"
              @click="colorPickerDialog[`js_${index}`] = true"
              :style="{ backgroundColor: formatColor(rule[1]), color: formatColor(rule[1]) ? '#fff' : undefined }"
              label="Color"
              hide-details
            />
            <v-dialog v-model="colorPickerDialog[`js_${index}`]" max-width="360px">
              <v-card>
                <v-card-title>Select Color</v-card-title>
                <v-card-text class="pa-0">
                  <v-color-picker class="w-100" width="100%" elevation="0"
                    :model-value="rule[1]"
                    @update:model-value="updateRule(rule, 1, $event, true); colorPickerDialog[`js_${index}`] = false"
                    mode="hexa"
                    show-swatches
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" text @click="colorPickerDialog[`js_${index}`] = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
          <v-col cols="1">
            <v-btn
              icon
              variant="text"
              color="error"
              @click="removeRule(index, true)"
              class="mt-2"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <v-btn variant="flat" color="primary" @click="addJsRule" class="mt-3">
        Add JS Expression Rule
      </v-btn>
    </div>


  </div>
</template>

<style scoped>
.property-list {
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 4px;
}

.property-item {
  cursor: pointer;
}

.property-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.1);
}

.active-tab {
  font-weight: bold;
  background-color: rgba(var(--v-theme-primary), 0.1);
}

.custom-tabs :deep(.v-tab--selected) {
  color: rgb(var(--v-theme-primary));
}
</style>
